import { WithAuth } from '@jotta/auth-client/WithAuth'
import { RouteBreadCrumbHandle } from '@jotta/hooks/useRouteBreadCrumb'
import { Trans } from '@lingui/macro'
import type { RouteObject } from 'react-router-dom'
import { NavLink } from 'react-router-dom'
import { PrivateLayout } from '../components/Layout/PrivateLayout'
import { BizLayout } from './BizLayout'
import { BizDashboardRoute } from './routes/BizDashboardRoute'
import { BizSettingsRoute } from './routes/BizSettingsRoute'
import { BizUsersRoute } from './routes/BizUsersRoute'
import Debug from 'debug'
import { bizApi } from './BizApi'
import { queryClient } from '@jotta/query'
import { get } from 'radash'
import { removeTrailingSlash } from '@jotta/utils/pathUtils'
import { BizUserCreateRoute } from './routes/BizUserCreateRoute'
import { BizUserEditRoute } from './routes/BizUserEditRoute'
import { BizBillingHistoryRoute } from './routes/BizBillingHistoryRoute'
import { BizOrderReceiptRoute } from './routes/BizOrderReceiptRoute'
import { BizChangePaymentInfoRoute } from './routes/BizChangePaymentInfoRoute'
import { BizChangeCreditCardAcceptedRoute } from './routes/BizChangeCreditCardAcceptedRoute'

const debug = Debug('jotta:biz:routes')

export const bizRoutes: RouteObject = {
  path: '/biz',
  element: (
    <WithAuth>
      <PrivateLayout section="biz" />
    </WithAuth>
  ),
  children: [
    {
      element: <BizLayout />,
      children: [
        {
          index: true,
          element: <BizDashboardRoute />,
        },
        {
          path: 'users',
          handle: {
            crumb: new RouteBreadCrumbHandle(() => (
              <NavLink end to="users">
                <Trans>Users</Trans>
              </NavLink>
            )),
          },
          children: [
            {
              index: true,
              element: <BizUsersRoute />,
            },
            {
              path: 'create',
              handle: {
                crumb: new RouteBreadCrumbHandle(({ pathname }) => {
                  return (
                    <NavLink end to={pathname}>
                      <Trans>Create new user</Trans>
                    </NavLink>
                  )
                }),
              },
              element: <BizUserCreateRoute />,
            },
            {
              path: ':username',
              loader: async ({ params }) => {
                const user = await queryClient.fetchQuery(
                  bizApi.user.byId.getFetchOptions({
                    username: params.username || '',
                  }),
                )
                return user
              },
              handle: {
                crumb: new RouteBreadCrumbHandle(
                  ({ pathname, params, data }) => {
                    const name = get(data, 'name', '')
                    return (
                      <NavLink end to={removeTrailingSlash(pathname)}>
                        {name}
                      </NavLink>
                    )
                  },
                ),
              },
              element: <BizUserEditRoute />,
            },
          ],
        },

        {
          path: 'settings',
          handle: {
            crumb: new RouteBreadCrumbHandle(({ pathname }) => {
              return (
                <NavLink end to={pathname}>
                  <Trans>Settings</Trans>
                </NavLink>
              )
            }),
          },
          children: [
            {
              index: true,
              element: <BizSettingsRoute />,
            },
            {
              path: 'paymentChange',
              handle: {
                crumb: new RouteBreadCrumbHandle(({ pathname }) => {
                  return (
                    <NavLink end to={pathname}>
                      <Trans>Change Payment Information</Trans>
                    </NavLink>
                  )
                }),
              },
              children: [
                {
                  index: true,
                  element: <BizChangePaymentInfoRoute />,
                },
                {
                  path: 'accepted',
                  element: <BizChangeCreditCardAcceptedRoute />,
                },
              ],
            },
            {
              path: 'billingHistory',
              handle: {
                crumb: new RouteBreadCrumbHandle(({ pathname }) => {
                  return (
                    <NavLink end to={pathname}>
                      <Trans>Billing history</Trans>
                    </NavLink>
                  )
                }),
              },
              children: [
                {
                  index: true,
                  element: <BizBillingHistoryRoute />,
                },
                {
                  path: ':orderNumber',
                  element: <BizOrderReceiptRoute />,
                  handle: {
                    crumb: new RouteBreadCrumbHandle(({ pathname, params }) => {
                      return (
                        <NavLink end to={pathname}>
                          {params.orderNumber}
                        </NavLink>
                      )
                    }),
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
}
